/**
 * HR-assigned "read this document" (and optionally "sign it and send it back") tasks —
 * deliberately NOT an OnboardingTask. Most people this needs to reach (an existing employee
 * acknowledging a revised policy) have no active OnboardingProcess, often never had one, and
 * tying this to that model would mean fabricating a process for people who aren't onboarding at
 * all. Works identically for a brand-new hire and a ten-year employee — the only difference is
 * whether the recipient also happens to have an onboarding checklist to see it alongside.
 *
 * Surfaced on the employee's My Onboarding page regardless of whether they have an onboarding
 * process (see apps/web/src/features/onboarding/MyOnboardingPage.tsx) and via a real notification
 * (document_task.assigned / document_task.completed) so the bell always tells the employee/HR
 * a document is waiting, exactly like every other module's notifications.
 */

import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import {
  dateOnlySchema,
  emailSchema,
  errorBodySchema,
  instantSchema,
  orgIdSchema,
  uuidSchema,
} from '../schemas/common.js';
import { routeMeta } from './meta.js';

const c = initContract();

export const documentTaskStatusSchema = z.enum(['pending', 'completed']);

export const documentTaskSchema = z.object({
  id: uuidSchema,
  orgId: orgIdSchema,
  personId: uuidSchema,
  personName: z.string(),
  /** Null for a phone-only employee (no work email). */
  personEmail: emailSchema.nullable(),
  assignedByPersonId: uuidSchema,
  assignedByName: z.string(),
  title: z.string(),
  description: z.string().nullable(),
  sourceDocumentUrl: z.string().url(),
  requiresSignature: z.boolean(),
  status: documentTaskStatusSchema,
  dueDate: dateOnlySchema,
  signedDocumentUrl: z.string().url().nullable(),
  completedAt: instantSchema.nullable(),
  createdAt: instantSchema,
});
export type DocumentTask = z.infer<typeof documentTaskSchema>;

export const createDocumentTaskBodySchema = z.object({
  personId: uuidSchema,
  title: z.string().trim().min(1).max(200),
  description: z.string().trim().max(2_000).optional(),
  sourceDocumentUrl: z.string().url(),
  requiresSignature: z.boolean().default(false),
  dueDate: dateOnlySchema,
});

/** Same shape as createDocumentTaskBodySchema minus personId — one task is created per active
 * employee in the org, each with its own row (so each person's own completion is independent). */
export const broadcastDocumentTaskBodySchema = z.object({
  title: z.string().trim().min(1).max(200),
  description: z.string().trim().max(2_000).optional(),
  sourceDocumentUrl: z.string().url(),
  requiresSignature: z.boolean().default(false),
  dueDate: dateOnlySchema,
});

export const completeDocumentTaskBodySchema = z.object({
  /** Required when the task's requiresSignature is true; ignored (a plain acknowledge) otherwise
   * — enforced server-side against the task's own flag, not trusted from the caller. */
  signedDocumentUrl: z.string().url().optional(),
});

export const listDocumentTasksQuerySchema = z.object({
  /** HR only — omit to get the caller's own tasks. A caller without onboarding:manage may only
   * ever pass their own personId, or none. */
  personId: uuidSchema.optional(),
  /** HR only — request tasks for all employees in the organization */
  all: z.coerce.boolean().optional(),
});

export const documentTaskUploadTicketSchema = z.object({
  uploadUrl: z.string().url(),
  documentUrl: z.string().url(),
  expiresIn: z.number().int().positive(),
});
export const createDocumentTaskUploadBodySchema = z.object({
  contentType: z.enum(['application/pdf', 'image/jpeg', 'image/png']),
  sizeBytes: z.number().int().positive().max(10_485_760),
});

export const documentTaskContract = c.router({
  createUploadTicket: {
    method: 'POST',
    path: '/document-tasks/upload-ticket',
    body: createDocumentTaskUploadBodySchema,
    responses: { 201: documentTaskUploadTicketSchema, 401: errorBodySchema, 422: errorBodySchema },
    summary: 'A presigned (or local-fallback) link to upload a source or signed document',
    metadata: routeMeta(null),
  },
  create: {
    method: 'POST',
    path: '/document-tasks',
    body: createDocumentTaskBodySchema,
    responses: {
      201: documentTaskSchema,
      401: errorBodySchema,
      403: errorBodySchema,
      404: errorBodySchema,
    },
    summary: 'HR assigns a person a document to read (and optionally sign and return)',
    metadata: routeMeta('onboarding:manage'),
  },
  broadcast: {
    method: 'POST',
    path: '/document-tasks/broadcast',
    body: broadcastDocumentTaskBodySchema,
    responses: {
      201: z.object({ assignedCount: z.number().int().nonnegative() }),
      401: errorBodySchema,
      403: errorBodySchema,
    },
    summary: 'HR assigns the same document to every active employee in the org at once',
    metadata: routeMeta('onboarding:manage'),
  },
  list: {
    method: 'GET',
    path: '/document-tasks',
    query: listDocumentTasksQuerySchema,
    responses: { 200: z.array(documentTaskSchema), 401: errorBodySchema, 403: errorBodySchema },
    metadata: routeMeta(null),
  },
  complete: {
    method: 'PATCH',
    path: '/document-tasks/:taskId/complete',
    body: completeDocumentTaskBodySchema,
    responses: {
      200: documentTaskSchema,
      401: errorBodySchema,
      403: errorBodySchema,
      404: errorBodySchema,
      422: errorBodySchema,
    },
    summary: "The employee's own acknowledge / upload-signed-copy action",
    metadata: routeMeta(null),
  },
});
